import React, { useState, useEffect } from 'react';
import { Button, Input, Empty, Spin, Card, Tag } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faGraduationCap, 
  faBook, 
  faLaptop, 
  faUserGraduate,
  faArrowRight,
  faSearch,
  faLightbulb,
  faCertificate,
  faUsers,
  faClock,
  faBookOpen,
  faStar,
  faChartLine,
  faAward,
  faRocket
} from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import axiosInstance from '../../configurations/axiosInstance/AxiosInstance';

const Home = () => {
  const [formations, setFormations] = useState([]);
  const [filteredFormations, setFilteredFormations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchPublishedFormations();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const filtered = formations.filter(formation =>
        formation.titre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (formation.description && formation.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredFormations(filtered);
    } else {
      setFilteredFormations(formations);
    }
  }, [searchTerm, formations]);

  const fetchPublishedFormations = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance().get('public/api/learn/formations/published');
      if (response.data && Array.isArray(response.data)) {
        setFormations(response.data);
        setFilteredFormations(response.data);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des formations:', error);
    } finally {
      setLoading(false);
    }
  };

  const getNiveauLabel = (niveau) => {
    const niveaux = {
      DEBUTANT: 'Débutant',
      INTERMEDIAIRE: 'Intermédiaire',
      AVANCE: 'Avancé',
      EXPERT: 'Expert'
    };
    return niveaux[niveau] || niveau;
  };

  const getNiveauColor = (niveau) => {
    const colors = {
      DEBUTANT: 'green',
      INTERMEDIAIRE: 'blue',
      AVANCE: 'orange',
      EXPERT: 'red'
    };
    return colors[niveau] || 'default';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-green-500 via-green-600 to-blue-600 overflow-hidden">
        {/* Animated Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute transform rotate-45 -left-20 -top-20 w-96 h-96 bg-white rounded-full"></div>
          <div className="absolute transform -rotate-45 -right-20 -bottom-20 w-96 h-96 bg-white rounded-full"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 py-20 md:py-32">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="text-white space-y-8">
              <div className="inline-block">
                <img 
                  src="/images/logo/logo.png" 
                  alt="YOUTHFP Logo" 
                  className="h-20 md:h-24 drop-shadow-2xl" 
                />
              </div>
              
              <div className="space-y-4">
                <h1 className="text-4xl md:text-6xl font-bold leading-tight">
                  Transformez votre avenir avec
                  <span className="block text-yellow-300">YOUTHFP E-Learning</span>
                </h1>
                <p className="text-lg md:text-xl text-white/90 leading-relaxed">
                  Accédez à des formations professionnelles de qualité, développez vos compétences et atteignez vos objectifs de carrière.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/login">
                  <Button 
                    size="large" 
                    className="h-14 px-8 text-lg font-semibold bg-white text-green-600 border-0 hover:bg-gray-100 shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
                  >
                    <FontAwesomeIcon icon={faRocket} className="mr-2" />
                    Commencer gratuitement
                  </Button>
                </Link>
                <a href="#formations">
                  <Button 
                    size="large" 
                    className="h-14 px-8 text-lg font-semibold bg-transparent text-white border-2 border-white hover:bg-white/10 shadow-xl"
                  >
                    Explorer les formations
                    <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                  </Button>
                </a>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-6 pt-8">
                <div className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-yellow-300">
                    {formations.length}+
                  </div>
                  <div className="text-sm text-white/80 mt-1">Formations</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-yellow-300">1000+</div>
                  <div className="text-sm text-white/80 mt-1">Apprenants</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-yellow-300">95%</div>
                  <div className="text-sm text-white/80 mt-1">Satisfaction</div>
                </div>
              </div>
            </div>

            {/* Right Content - Illustration */}
            <div className="hidden md:block">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-yellow-300/20 to-blue-300/20 blur-3xl rounded-full"></div>
                <img 
                  src="/images/Best-online-course-platforms.png" 
                  alt="E-Learning" 
                  className="relative w-full h-auto rounded-2xl shadow-2xl transform hover:scale-105 transition-transform duration-500"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="max-w-7xl mx-auto px-4 py-16 -mt-10">
        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-white rounded-2xl shadow-xl p-8 transform hover:-translate-y-2 transition-all duration-300 border-t-4 border-green-500">
            <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mb-6">
              <FontAwesomeIcon icon={faLaptop} className="text-3xl text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Apprentissage Flexible</h3>
            <p className="text-gray-600 leading-relaxed">
              Apprenez à votre rythme, où vous voulez et quand vous voulez avec nos cours en ligne accessibles 24/7.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8 transform hover:-translate-y-2 transition-all duration-300 border-t-4 border-blue-500">
            <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mb-6">
              <FontAwesomeIcon icon={faUserGraduate} className="text-3xl text-blue-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Formateurs Experts</h3>
            <p className="text-gray-600 leading-relaxed">
              Bénéficiez de l'expertise de professionnels reconnus dans leurs domaines respectifs.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8 transform hover:-translate-y-2 transition-all duration-300 border-t-4 border-yellow-500">
            <div className="w-16 h-16 bg-yellow-100 rounded-2xl flex items-center justify-center mb-6">
              <FontAwesomeIcon icon={faCertificate} className="text-3xl text-yellow-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Certifications Reconnues</h3>
            <p className="text-gray-600 leading-relaxed">
              Obtenez des certificats valorisant vos compétences et boostant votre carrière professionnelle.
            </p>
          </div>
        </div>
      </div>

      {/* Search Section */}
      <div id="formations" className="max-w-7xl mx-auto px-4 py-12">
        <div className="bg-white rounded-3xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
              Découvrez Nos Formations
            </h2>
            <p className="text-lg text-gray-600">
              {formations.length} formation{formations.length > 1 ? 's' : ''} disponible{formations.length > 1 ? 's' : ''} pour vous
            </p>
          </div>

          {/* <div className="max-w-2xl mx-auto">
            <Input
              size="large"
              placeholder="Rechercher une formation par titre ou description..."
              prefix={<FontAwesomeIcon icon={faSearch} className="text-gray-400 mr-2" />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="rounded-xl text-lg py-3"
              allowClear
            />
          </div> */}
        </div>
      </div>

      {/* Formations Grid */}
      <div className="max-w-7xl mx-auto px-4 pb-16">
        {loading ? (
          <div className="text-center py-20">
            <Spin size="large" />
            <p className="text-gray-600 mt-4">Chargement des formations...</p>
          </div>
        ) : filteredFormations.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredFormations.map((formation) => (
              <div 
                key={formation.id} 
                className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden transform hover:-translate-y-2"
              >
                {/* Image */}
                <div className="relative h-48 overflow-hidden bg-gradient-to-br from-green-400 to-blue-500">
                  <img
                    src={formation.imageCouverture || "/images/Best-online-course-platforms.png"}
                    alt={formation.titre}
                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                  
                  {/* Niveau Badge */}
                  <div className="absolute top-4 right-4">
                    <Tag color={getNiveauColor(formation.niveau)} className="font-semibold px-3 py-1 text-sm">
                      {getNiveauLabel(formation.niveau)}
                    </Tag>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6 space-y-4">
                  {/* Category */}
                  {formation.categories && (
                    <div className="flex items-center gap-2">
                      <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold uppercase tracking-wide">
                        {formation.categories.libelle || 'Formation'}
                      </span>
                    </div>
                  )}

                  {/* Title */}
                  <h3 className="text-xl font-bold text-gray-900 line-clamp-2 min-h-[3.5rem] group-hover:text-green-600 transition-colors">
                    {formation.titre}
                  </h3>

                  {/* Description */}
                  {formation.description && (
                    <p className="text-gray-600 text-sm line-clamp-2 leading-relaxed">
                      {formation.description}
                    </p>
                  )}

                  {/* Info */}
                  <div className="flex items-center gap-4 text-sm text-gray-500 border-t pt-4">
                    <div className="flex items-center gap-2">
                      <FontAwesomeIcon icon={faBookOpen} className="text-green-500" />
                      <span>{formation.nombreModules || 0} modules</span>
                    </div>
                    {formation.dureeEstimee && (
                      <div className="flex items-center gap-2">
                        <FontAwesomeIcon icon={faClock} className="text-blue-500" />
                        <span>{formation.dureeEstimee}h</span>
                      </div>
                    )}
                  </div>

                  {/* CTA Button */}
                  <Link to="/login">
                    <Button
                      block
                      size="large"
                      className="h-12 font-semibold bg-gradient-to-r from-green-500 to-blue-500 text-white border-0 hover:from-green-600 hover:to-blue-600 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
                    >
                      <FontAwesomeIcon icon={faGraduationCap} className="mr-2" />
                      Commencer cette formation
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <Empty
              description={
                <div className="text-gray-500">
                  <p className="text-xl font-semibold mb-2">Aucune formation trouvée</p>
                  <p className="text-sm">
                    {searchTerm ? "Essayez avec d'autres mots-clés" : "Aucune formation n'est disponible pour le moment"}
                  </p>
                </div>
              }
            />
          </div>
        )}
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-green-500 to-blue-600 py-20">
        <div className="max-w-4xl mx-auto px-4 text-center text-white">
          <FontAwesomeIcon icon={faAward} className="text-6xl mb-6 text-yellow-300" />
          <h2 className="text-3xl md:text-5xl font-bold mb-6">
            Prêt à commencer votre parcours d'apprentissage ?
          </h2>
          <p className="text-xl mb-8 text-white/90 leading-relaxed">
            Rejoignez des milliers d'apprenants qui ont déjà transformé leur carrière avec YOUTHFP E-Learning
          </p>
          <Link to="/login">
            <Button
              size="large"
              className="h-14 px-10 text-lg font-semibold bg-white text-green-600 border-0 hover:bg-gray-100 shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all duration-300"
            >
              <FontAwesomeIcon icon={faRocket} className="mr-2" />
              Inscrivez-vous gratuitement
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Home;